import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { EmitInvoiceRequest, IInvoice } from "../library/models/invoice";
import {
    IInvoicingSettings,
    ITestConnectionResult,
    InvoicingSettingsRequest,
    TestConnectionRequest,
} from "../library/models/invoicingSettings";
import { BaseService } from "./baseService";

/** What the emit dialog hands over: the details, and the photograph, in one request. */
export interface EmitInvoiceVariables {
    request: EmitInvoiceRequest;
    file: File;
}

/**
 * How a clinic issues invoices, and every invoice it has issued.
 *
 * No route names a clinic: the API takes it from the session, so this reads the caller's own
 * settings and writes to nobody else's. The settings half is the clinic admin's alone and the
 * API refuses either way, rather than trusting the tab to have been hidden.
 */
export class InvoicingService extends BaseService {
    /**
     * Keyed by the clinic even though the request does not name one -- the same trap
     * `TenantBrandingService.getBranding` documents: without the tenant in the key, somebody
     * signing out of one clinic and into another in a single page load is served the first
     * clinic's settings from cache. Hand-written rather than built with createGetQuery, whose
     * key is the URL and its query parameters, because the tenant is neither.
     */
    getSettings(tenantId: number | null): UseQueryOptions<IInvoicingSettings, Error> {
        return {
            queryKey: ['/invoicing-settings', tenantId],
            queryFn: (): Promise<IInvoicingSettings> =>
                this.get<IInvoicingSettings>('/invoicing-settings'),
        };
    }

    /**
     * Hand-written rather than built with createMutation, which only speaks POST/PATCH/DELETE
     * shapes. Resolves with the stored result so the caller renders what was saved rather than
     * trusting its own draft.
     */
    saveSettingsMutation(): UseMutationOptions<IInvoicingSettings, Error, InvoicingSettingsRequest> {
        return {
            mutationFn: (payload: InvoicingSettingsRequest): Promise<IInvoicingSettings> =>
                this.put<IInvoicingSettings>('/invoicing-settings', payload),
        };
    }

    /**
     * Asks the provider whether the credentials work, and what the account can invoice as.
     *
     * Stores nothing, which is the point: an admin can find out whether a secret they have
     * just typed works before committing to it. What comes back is the fiscal code and the
     * series list, so the two fields nobody knows by heart become pickers instead of free
     * text somebody discovers was wrong when an invoice is refused.
     */
    testConnectionMutation(): UseMutationOptions<ITestConnectionResult, Error, TestConnectionRequest> {
        return {
            mutationFn: (payload: TestConnectionRequest): Promise<ITestConnectionResult> =>
                this.post<ITestConnectionResult>('/invoicing-settings/test', payload),
        };
    }

    /**
     * Every invoice for one patient, cancelled ones included.
     *
     * One request per patient file rather than one per purchase card: a patient with six
     * purchases would otherwise open with six requests, and the card knows which invoice is
     * its own from `purchaseId`.
     */
    getInvoicesForPatient(patientId: number): UseQueryOptions<IInvoice[], Error> {
        return {
            queryKey: ['/invoices', patientId],
            queryFn: (): Promise<IInvoice[]> =>
                this.get<IInvoice[]>('/invoices', { params: { patientId } }),
        };
    }

    /**
     * Emits the invoice.
     *
     * Multipart, with the details as a JSON part beside the photograph. The `Content-Type:
     * undefined` is not a mistake and is the same gotcha `TenantBrandingService.uploadLogo`
     * documents: the browser has to generate the multipart boundary itself, and it will not
     * if a Content-Type is already set.
     *
     * The JSON part is wrapped in a Blob with an explicit type, or the server reads it as a
     * plain string field and cannot bind it to the request object.
     */
    emitInvoiceMutation(): UseMutationOptions<IInvoice, Error, EmitInvoiceVariables> {
        return {
            mutationFn: (variables: EmitInvoiceVariables): Promise<IInvoice> => {
                const body: FormData = new FormData();

                body.append(
                    'request',
                    new Blob([JSON.stringify(variables.request)], { type: 'application/json' })
                );
                body.append('file', variables.file);

                return this.post<IInvoice>('/invoices', body, {
                    headers: { 'Content-Type': undefined },
                });
            },
        };
    }

    /**
     * Tries a failed invoice again. Takes no body: the details and the photograph are the ones
     * already stored, and so is the idempotency key -- which is what makes retrying after a
     * call that timed out safe rather than a way to end up with two documents.
     */
    retryInvoiceMutation(): UseMutationOptions<IInvoice, Error, number> {
        return {
            mutationFn: (invoiceId: number): Promise<IInvoice> =>
                this.post<IInvoice>(`/invoices/${invoiceId}/retry`, {}),
        };
    }

    /** Withdraws an issued invoice at the provider. Administrators only; the API enforces it. */
    cancelInvoiceMutation(): UseMutationOptions<IInvoice, Error, number> {
        return {
            mutationFn: (invoiceId: number): Promise<IInvoice> =>
                this.post<IInvoice>(`/invoices/${invoiceId}/cancel`, {}),
        };
    }

    /**
     * Fetches the archived photograph as a blob URL the browser can render.
     *
     * The endpoint is authenticated, so an `<img src>` pointed straight at it would 401 -- a
     * browser rendering an image sends no Authorization header. Going through the axios
     * instance and handing the result to `createObjectURL` is what lets the picture be shown
     * at all. **The caller owns revoking the URL**, or every peek at a receipt leaks a few
     * hundred kilobytes for the life of the tab.
     */
    async fetchReceiptObjectUrl(invoiceId: number): Promise<string> {
        const blob: Blob = await this.get<Blob>(`/invoices/${invoiceId}/receipt`, {
            responseType: 'blob',
        });

        return URL.createObjectURL(blob);
    }
}
